const bridge = () =>
  typeof window !== "undefined" ? window.desktopOfflineProducts : null;

const requireBridge = () => {
  const api = bridge();
  if (!api) throw new Error("Offline product storage is unavailable.");
  return api;
};

const cleanOfflineProductError = (error) => {
  const message = String(error?.message || error || "Offline product operation failed.")
    .replace(/^Error invoking remote method '[^']+':\s*(?:Error:\s*)?/i, "")
    .replace(/^Error:\s*/i, "")
    .trim();
  return new Error(message || "Offline product operation failed.");
};

const invoke = async (operation) => {
  try {
    return await operation();
  } catch (error) {
    throw cleanOfflineProductError(error);
  }
};

export const hasOfflineProductStore = () => Boolean(bridge());
export const listOfflineProducts = (restaurantId) =>
  invoke(() => requireBridge().list({ restaurantId }));

const publishCatalogChange = (detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("desktop-product-sync-change", { detail }));
};

export const createOfflineProduct = async (restaurantId, product) => {
  const result = await invoke(() => requireBridge().create({ restaurantId, product }));
  publishCatalogChange({
    type: "offline-product-created",
    restaurantId,
    productId: result?.product?.id,
  });
  return result;
};

export const updateOfflineProduct = async (restaurantId, productId, product) => {
  const result = await invoke(() => requireBridge().update({ restaurantId, productId, product }));
  publishCatalogChange({ type: "offline-product-updated", restaurantId, productId });
  return result;
};

export const deleteOfflineProduct = async (restaurantId, productId) => {
  const result = await invoke(() => requireBridge().remove({ restaurantId, productId }));
  if (result?.success === false) {
    throw new Error(result?.message || "Unable to delete the offline product.");
  }
  publishCatalogChange({ type: "offline-product-deleted", restaurantId, productId });
  return result;
};

export const bulkUpdateOfflineProductTax = async (
  restaurantId,
  productIds,
  gstPercentage,
  taxChoice,
  genericTax = false
) => {
  const api = requireBridge();
  if (!api.bulkTaxUpdate) {
    throw new Error("Restart the desktop app to initialize offline bulk tax updates.");
  }
  const result = await invoke(() => api.bulkTaxUpdate({
    restaurantId,
    productIds,
    gstPercentage,
    taxChoice,
    genericTax,
  }));
  publishCatalogChange({
    type: "offline-products-bulk-tax-updated",
    restaurantId,
    productIds,
  });
  return result;
};

export const reorderOfflineProducts = async (restaurantId, items) => {
  const api = requireBridge();
  if (!api.reorder) {
    throw new Error("Restart the desktop app to initialize offline product reorder.");
  }
  const result = await invoke(() => api.reorder({ restaurantId, items }));
  publishCatalogChange({
    type: "offline-products-reordered",
    restaurantId,
    productIds: (Array.isArray(items) ? items : []).map((item) => item?.productid),
  });
  return result;
};
